import styled, { css } from "styled-components";
import { StatusPill } from "../../atoms/StatusPill";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import {
  borderRadius,
  borderStyle,
  borderWidth,
} from "../../../design-system/tokens/borders";
import { shadows } from "../../../design-system/tokens/shadows";
import { typographyMixin } from "../../../design-system/tokens/typography";
import type {
  StoryTemplateCardKind,
  StoryTemplateCardPresentation,
} from "./StoryTemplateCard.types";

export const Card = styled.article<{
  $interactive: boolean;
  $presentation: StoryTemplateCardPresentation;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  background: ${({ theme }) =>
    theme.colors?.background?.primary ?? rawColors.structural.white};
  border: ${borderWidth[1]} ${borderStyle.solid} ${rawColors.stroke[200]};
  border-radius: ${borderRadius.xl};
  box-shadow: ${shadows.component.card.default};
  cursor: ${({ $interactive }) => ($interactive ? "pointer" : "default")};

  ${({ $interactive }) =>
    $interactive &&
    css`
      &:hover {
        border-color: ${rawColors.stroke[300]};
        box-shadow: ${shadows.component.card.hover};
      }

      &:focus-visible {
        outline: ${borderWidth[2]} ${borderStyle.solid}
          ${rawColors.primary[500]};
        outline-offset: ${spacing[0.5]};
      }
    `}
`;

export const CardBody = styled.div<{
  $presentation: StoryTemplateCardPresentation;
}>`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: ${spacing[2]};
  min-width: 0;
  /* Picker cards must total 7.25rem like the blank/AI cards: 6.25rem body + 2 x spacing[2] padding. */
  min-height: ${({ $presentation }) =>
    $presentation === "picker" ? "6.25rem" : "0"};
  box-sizing: border-box;
  padding: ${({ $presentation }) =>
    $presentation === "picker" ? spacing[2] : spacing[3]};
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  min-width: 0;
`;

export const ImportantIcon = styled.img<{
  $presentation: StoryTemplateCardPresentation;
}>`
  width: ${({ $presentation }) =>
    $presentation === "picker" ? spacing[3] : "0.875rem"};
  height: ${({ $presentation }) =>
    $presentation === "picker" ? spacing[3] : "0.8125rem"};
  flex-shrink: 0;
`;

export const Title = styled.h3<{
  $presentation: StoryTemplateCardPresentation;
}>`
  flex: 0 1 auto;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: ${({ theme }) =>
    theme.colors?.text?.primary ?? rawColors.text.highEmphasis};
  ${({ $presentation }) =>
    $presentation === "picker"
      ? typographyMixin("caption", 3)
      : typographyMixin("body", 2)}
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const KindPill = styled(StatusPill)<{
  $kind: StoryTemplateCardKind;
}>`
  flex-shrink: 0;
  padding: ${spacing[1]} ${spacing[2]};
  border: ${borderWidth[1]} ${borderStyle.solid} transparent;
  border-radius: ${borderRadius.full};
  ${({ $kind }) =>
    $kind === "custom"
      ? css`
          background: ${rawColors.accent.pinkLight};
          color: ${rawColors.accent.pink};
        `
      : $kind === "imported"
        ? css`
            background: ${rawColors.primary[50]};
            color: ${rawColors.text.interactive};
          `
      : css`
          background: ${rawColors.structural[100]};
          color: ${rawColors.primary[500]};
        `}
`;

export const MenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${spacing[6]};
  height: ${spacing[6]};
  margin-left: auto;
  padding: 0;
  flex-shrink: 0;
  color: ${rawColors.text.mediumEmphasis};
  background: transparent;
  border: 0;
  border-radius: ${borderRadius.md};
  cursor: pointer;

  &:hover {
    background: ${rawColors.structural[100]};
  }

  &:focus-visible {
    outline: ${borderWidth[2]} ${borderStyle.solid} ${rawColors.primary[500]};
  }
`;

export const Preview = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  height: 8.5rem;
  box-sizing: border-box;
  padding: ${spacing[3]};
  overflow: hidden;
  background: ${rawColors.structural.themeLight};
  border-radius: ${borderRadius.xl};
`;

export const PreviewBar = styled.div<{ $accent?: boolean }>`
  width: 100%;
  height: 0.625rem;
  flex-shrink: 0;
  border-radius: ${borderRadius.sm};
  background: ${({ $accent }) =>
    $accent ? rawColors.primary[200] : rawColors.primary[100]};
  opacity: ${({ $accent }) => ($accent ? 0.7 : 0.8)};
`;

export const PreviewRows = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.375rem;
  min-height: 0;
`;

export const PreviewRow = styled.div<{ $height?: "large" | "compact" }>`
  display: flex;
  flex: ${({ $height }) =>
    $height === "large"
      ? "0 0 3.625rem"
      : $height === "compact"
        ? "0 0 1.375rem"
        : "1 1 0"};
  gap: 0.375rem;
  min-height: 0;
`;

export const PreviewCell = styled.div<{
  $accent?: boolean;
  $width?: "narrow" | "wide";
}>`
  flex: ${({ $width }) =>
    $width === "narrow" ? "0 0 3rem" : $width === "wide" ? "2 1 0" : "1 1 0"};
  min-width: 0;
  border-radius: ${borderRadius.sm};
  background: ${({ $accent }) =>
    $accent ? rawColors.primary[200] : rawColors.primary[100]};
  opacity: ${({ $accent }) => ($accent ? 0.7 : 0.8)};
`;

export const PreviewStack = styled.div`
  display: flex;
  flex: 0 0 3rem;
  flex-direction: column;
  gap: 0.375rem;
`;

export const PickerPreview = styled.div<{ $centered?: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: ${({ $centered }) => ($centered ? "center" : "stretch")};
  justify-content: center;
  gap: ${spacing[2]};
  min-height: 0;
`;

export const PickerSystemBar = styled.div`
  width: 100%;
  height: 0.75rem;
  flex-shrink: 0;
  background: ${rawColors.primary[100]};
  border-radius: ${borderRadius.full};
`;

export const PickerSystemRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  width: 100%;
`;

export const PickerSystemRow = styled.div`
  display: flex;
  gap: 0.375rem;
  height: 1.75rem;
`;

export const PickerSystemCell = styled.div<{ $narrow?: boolean }>`
  flex: ${({ $narrow }) => ($narrow ? "0 0 5.875rem" : "1 1 auto")};
  min-width: 0;
  background: ${({ $narrow }) =>
    $narrow ? rawColors.primary[200] : rawColors.primary[100]};
  border-radius: ${borderRadius.lg};
`;

export const PickerCustomElement = styled.div<{
  $hero?: boolean;
}>`
  width: 62.5%;
  max-width: 15rem;
  height: ${({ $hero }) => ($hero ? "3.5rem" : "0.625rem")};
  flex-shrink: 0;
  background: ${({ $hero }) =>
    $hero ? rawColors.primary[200] : rawColors.primary[100]};
  border-radius: ${({ $hero }) =>
    $hero ? borderRadius.lg : borderRadius.full};
`;

export const RecipePreviewStack = styled.div<{ $narrow?: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  gap: 0.1875rem;
  width: ${({ $narrow }) => ($narrow ? "7.4375rem" : "100%")};
  min-height: 0;
  margin-inline: auto;
`;

export const RecipePreviewBar = styled.div<{
  $accent?: boolean;
  $height?: "five" | "six" | "ten";
}>`
  width: 100%;
  height: ${({ $height = "six" }) =>
    $height === "five"
      ? "0.3125rem"
      : $height === "ten"
        ? "0.625rem"
        : "0.375rem"};
  flex-shrink: 0;
  background: ${({ $accent }) =>
    $accent ? rawColors.primary[200] : rawColors.primary[100]};
  border-radius: ${borderRadius.sm};
  opacity: ${({ $accent }) => ($accent ? 0.7 : 0.8)};
`;

export const RecipePreviewRow = styled.div<{ $height: "fourteen" | "sixteen" | "thirty" }>`
  display: flex;
  gap: 0.1875rem;
  width: 100%;
  height: ${({ $height }) =>
    $height === "fourteen"
      ? "0.875rem"
      : $height === "sixteen"
        ? "1rem"
        : "1.875rem"};
  flex-shrink: 0;
`;

export const RecipePreviewCell = styled.div<{
  $accent?: boolean;
  $narrow?: boolean;
}>`
  flex: ${({ $narrow }) => ($narrow ? "0 0 2.9375rem" : "1 1 0")};
  min-width: 0;
  background: ${({ $accent }) =>
    $accent ? rawColors.primary[200] : rawColors.primary[100]};
  border-radius: ${borderRadius.sm};
  opacity: ${({ $accent }) => ($accent ? 0.7 : 0.8)};
`;

export const RecipePreviewHero = styled.div`
  width: 100%;
  height: 1.75rem;
  flex-shrink: 0;
  background: ${rawColors.primary[200]};
  border-radius: ${borderRadius.sm};
  opacity: 0.7;
`;

export const RecipePreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1875rem;
`;

export const RecipePreviewColumn = styled(RecipePreviewSection)`
  flex: 0 0 2.9375rem;
`;

export const Description = styled.p`
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: ${({ theme }) =>
    theme.colors?.text?.secondary ?? rawColors.text.mediumEmphasis};
  ${typographyMixin("body", 4)}
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

export const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 3rem;
  box-sizing: border-box;
  padding: ${spacing[3]};
  border-top: ${borderWidth[1]} ${borderStyle.solid} ${rawColors.stroke[200]};
`;

export const Owner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
`;

export const MagicBiLogo = styled.img`
  width: ${spacing[5]};
  height: ${spacing[5]};
  flex-shrink: 0;
  object-fit: contain;
`;

export const OwnerAvatar = styled.img`
  width: ${spacing[5]};
  height: ${spacing[5]};
  box-sizing: border-box;
  flex-shrink: 0;
  object-fit: cover;
  border: ${borderWidth[1]} ${borderStyle.solid} ${rawColors.structural.white};
  border-radius: ${borderRadius.full};
`;

export const OwnerInitials = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${spacing[5]};
  height: ${spacing[5]};
  box-sizing: border-box;
  flex-shrink: 0;
  color: ${rawColors.primary[500]};
  background: ${rawColors.primary[100]};
  border: ${borderWidth[1]} ${borderStyle.solid} ${rawColors.structural.white};
  border-radius: ${borderRadius.full};
  ${typographyMixin("overline", 4)}
  text-transform: uppercase;
`;

export const OwnerName = styled.span`
  overflow: hidden;
  color: ${({ theme }) =>
    theme.colors?.text?.primary ?? rawColors.text.highEmphasis};
  ${typographyMixin("caption", 3)}
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StoryCount = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  flex-shrink: 0;
  color: ${({ theme }) =>
    theme.colors?.text?.primary ?? rawColors.text.highEmphasis};
  ${typographyMixin("body", 3)}
`;

export const StoryCountIcon = styled.img`
  width: 0.875rem;
  height: 0.875rem;
  flex-shrink: 0;
`;

export const StoryCountLabel = styled.span`
  color: ${({ theme }) =>
    theme.colors?.text?.secondary ?? rawColors.text.mediumEmphasis};
  ${typographyMixin("body", 4)}
`;
